import { App } from "antd"
import { type MutableRefObject, useCallback, useEffect, useRef } from "react"
import { useTranslation } from "react-i18next"
import type { BlockerFunction } from "react-router-dom"

import usePrompt from "../../../hooks/use-prompt.ts"

interface UnsavedChangesGuardRefs {
  dirtyRef: MutableRefObject<boolean>
  /** Set while a save or other request runs; leaving then is held off. */
  busyRef: MutableRefObject<unknown>
  /** Set while the page moves its own address after a save. */
  ownNavigationRef: MutableRefObject<boolean>
}

// Asks before an in-app navigation (links, sidebar, tabs, back) leaves unsaved
// changes behind, and has the browser ask before the tab closes. Reads refs
// so a save that navigates in the same tick is not blocked by a stale render.
// While a save is still running, leaving is held off with a note to wait:
// "discard" would be untrue, since the save may still go through.
export function useUnsavedChangesGuard({
  dirtyRef,
  busyRef,
  ownNavigationRef
}: UnsavedChangesGuardRefs) {
  const { t } = useTranslation()
  const { modal } = App.useApp()

  const shouldBlock = useCallback<BlockerFunction>(
    ({ currentLocation, nextLocation }) =>
      !ownNavigationRef.current &&
      (dirtyRef.current || !!busyRef.current) &&
      currentLocation.pathname !== nextLocation.pathname,
    [dirtyRef, busyRef, ownNavigationRef]
  )
  const blocker = usePrompt(shouldBlock)
  const blockerRef = useRef(blocker)
  blockerRef.current = blocker

  useEffect(() => {
    if (blocker.state !== "blocked") return
    if (busyRef.current) {
      modal.info({
        centered: true,
        title: t("settingsAutomation.builder.unsaved.stillSavingTitle"),
        content: t("settingsAutomation.builder.unsaved.stillSavingBody"),
        okText: t("settingsAutomation.builder.unsaved.stillSavingOk"),
        onOk: () => blockerRef.current.reset?.(),
        onCancel: () => blockerRef.current.reset?.()
      })
      return
    }
    modal.confirm({
      centered: true,
      title: t("settingsAutomation.builder.unsaved.title"),
      content: t("settingsAutomation.builder.unsaved.body"),
      okText: t("settingsAutomation.builder.unsaved.discard"),
      okButtonProps: { danger: true },
      cancelText: t("settingsAutomation.builder.unsaved.keepEditing"),
      onOk: () => blockerRef.current.proceed?.(),
      onCancel: () => blockerRef.current.reset?.()
    })
  }, [blocker.state, busyRef, modal, t])
}
